import type { CeoDebtPortfolioSummary } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import {
  formatDateRu,
  formatMillions,
  INSTRUMENT_TYPE_LABELS,
} from '../ceoFormat';
import { WrapBadge } from '../ui';
import {
  CardSubtext,
  ContractNumberSpan,
  InstrumentDetails,
  InstrumentHeader,
  InstrumentRow,
  InstrumentsList,
  InstrumentTitle,
  ItemInfo,
  ItemSide,
  SectionHeaderMb,
  SectionLink,
  SectionTitle,
  StatTile,
  StatTiles,
  StatTileSuccess,
  TileLabel,
  TileValue,
  TileValueSuccess,
} from './styles';

interface DebtPortfolioCardProps {
  debtSum: CeoDebtPortfolioSummary;
  onSelectDebt: (id: string) => void;
}

export function DebtPortfolioCard({ debtSum, onSelectDebt }: DebtPortfolioCardProps) {
  return (
    <Card>
      <SectionHeaderMb>
        <div>
          <SectionTitle>Календарь и портфель кредитов и лизингов</SectionTitle>
          <CardSubtext>
            Кредиты: {formatMillions(debtSum.total_credit_principal)} · Лизинг (
            {debtSum.leased_units_total} ед.): {formatMillions(debtSum.total_leasing_principal)}{' '}
            · БГ: {formatMillions(debtSum.total_guarantees_amount)} · Средневзв. ставка:{' '}
            <strong>{debtSum.weighted_avg_rate_pct.toFixed(2)}%</strong>
          </CardSubtext>
        </div>
        <SectionLink to="/agents/ceo/debts">Весь календарь →</SectionLink>
      </SectionHeaderMb>

      <StatTiles>
        <StatTile>
          <TileLabel>Ближайшие 7 дней</TileLabel>
          <TileValue>
            {formatMillions(debtSum.payments_next_7d)}
          </TileValue>
        </StatTile>
        <StatTile>
          <TileLabel>Ближайшие 30 дней</TileLabel>
          <TileValue>
            {formatMillions(debtSum.payments_next_30d)}
          </TileValue>
        </StatTile>
        <StatTileSuccess>
          <TileLabel>Свободный лимит ВКЛ</TileLabel>
          <TileValueSuccess>
            {formatMillions(debtSum.available_credit_limit)}
          </TileValueSuccess>
        </StatTileSuccess>
      </StatTiles>

      <InstrumentsList>
        {debtSum.instruments.map((inst) => (
          <InstrumentRow key={inst.id}>
            <ItemInfo>
              <InstrumentHeader>
                <WrapBadge
                  $tone={inst.instrument_type === 'leasing' ? 'info' : 'warning'}
                >
                  {INSTRUMENT_TYPE_LABELS[inst.instrument_type] ?? inst.instrument_type}
                </WrapBadge>
                <strong>{inst.creditor_name}</strong>
                <ContractNumberSpan>№ {inst.contract_number}</ContractNumberSpan>
              </InstrumentHeader>
              <InstrumentTitle>{inst.title}</InstrumentTitle>
              <InstrumentDetails>
                {inst.instrument_type === 'bank_guarantee' ? (
                  <>
                    Лимит гарантии: <strong>{formatMillions(inst.total_limit)}</strong> ·
                    Комиссия:{' '}
                  </>
                ) : (
                  <>
                    Остаток: <strong>{formatMillions(inst.principal_balance)}</strong> ·
                    Ставка:{' '}
                  </>
                )}
                <strong>{inst.effective_rate_pct.toFixed(2)}%</strong> · След. платёж:{' '}
                <strong>
                  {inst.next_payment_date
                    ? `${formatDateRu(inst.next_payment_date)} (${formatMillions(inst.next_payment_amount)})`
                    : '—'}
                </strong>
              </InstrumentDetails>
            </ItemInfo>
            <ItemSide>
              <Button
                $size="sm"
                $variant="secondary"
                onClick={() => onSelectDebt(inst.id)}
              >
                Вся информация
              </Button>
            </ItemSide>
          </InstrumentRow>
        ))}
      </InstrumentsList>
    </Card>
  );
}
